import React from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { Booking, BookingFormData, BookingMode, Combo6h1hOption, Room, RoomType } from "@/types/schedule";
import { getRoomPriceConfig } from "@/types/schedule";
import { ShoppingCart, Clock, Check, AlertCircle, Lock } from "lucide-react";
import { calculateDuration, formatDateInput, formatPrice } from "@/utils/helpers";
import { BOOKING_MODES, HOURS, MINUTES } from "./constants";
import { RoomTypeBadge } from "@/components/rooms/RoomTypeBadge";
import { getAmenityIcon, hasSharedWC, isSharedWC, SHARED_WC_WARNING } from "@/data/amenities";
import type { BlockDefinition } from "@/services/blockService";

/** Cộng offsetHours vào checkInDate+checkInTime, trả về checkOutDate+checkOutTime */
function addHoursToCheckIn(checkInDate: Date, checkInTime: string, offsetHours: number): { date: Date; time: string } {
    const [h, m] = checkInTime.split(":").map(Number);
    const base = new Date(checkInDate.getFullYear(), checkInDate.getMonth(), checkInDate.getDate(), h, m, 0, 0);
    const out = new Date(base.getTime() + offsetHours * 3600 * 1000);
    const hh = String(out.getHours()).padStart(2, "0");
    const mm = String(out.getMinutes()).padStart(2, "0");
    return { date: new Date(out.getFullYear(), out.getMonth(), out.getDate()), time: `${hh}:${mm}` };
}

interface TimeSelectsProps {
    field: 'checkInTime' | 'checkOutTime';
    time: string;
    onTimeChange: (field: 'checkInTime' | 'checkOutTime', value: string) => void;
}

const TimeSelects: React.FC<TimeSelectsProps> = ({ field, time, onTimeChange }) => (
    <>
        <Select value={time.split(":")[0] + "h"} onValueChange={v => onTimeChange(field, `${v.replace("h", "")}:${time.split(":")[1] || "00"}`)}>
            <SelectTrigger className="w-[66px] px-1.5 shrink-0 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent position="popper" align="start">{HOURS.map(h => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={time.split(":")[1] || "00"} onValueChange={v => onTimeChange(field, `${time.split(":")[0]}:${v}`)}>
            <SelectTrigger className="w-[58px] px-1.5 shrink-0 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent position="popper" align="start">{MINUTES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
        </Select>
    </>
);

interface Step1Props {
    formData: BookingFormData;
    setFormData: React.Dispatch<React.SetStateAction<BookingFormData>>;
    rooms: Room[];
    bookings?: Booking[];
    selectedDate?: Date;
    price: number;
    onOpenFoodModal?: () => void;
    blockDefinitions?: BlockDefinition[];
}

export const Step1: React.FC<Step1Props> = ({ formData, setFormData, rooms, bookings = [], price, onOpenFoodModal, blockDefinitions = [] }) => {
    const duration = calculateDuration(formData.checkInDate, formData.checkInTime, formData.checkOutDate, formData.checkOutTime);
    const shortStayDiscount = formData.mode === 'hourly'
        ? (duration < 2 ? 20000 : (duration < 3 ? 10000 : 0))
        : 0;
    const originalPrice = price + shortStayDiscount;

    const [modeData, setModeData] = React.useState<Record<BookingMode, { checkInDate: Date; checkInTime: string; checkOutDate: Date; checkOutTime: string }>>(() => {
        const combo3hOut = addHoursToCheckIn(formData.checkInDate, formData.checkInTime, 3);
        const combo6h1hOut = addHoursToCheckIn(formData.checkInDate, formData.checkInTime, 7);
        return {
            hourly: { checkInDate: formData.checkInDate, checkInTime: formData.checkInTime, checkOutDate: formData.checkOutDate, checkOutTime: formData.checkOutTime },
            daily: { checkInDate: formData.checkInDate, checkInTime: '14:00', checkOutDate: new Date(formData.checkInDate.getTime() + 86400000), checkOutTime: '12:00' },
            overnight: { checkInDate: formData.checkInDate, checkInTime: '22:00', checkOutDate: new Date(formData.checkInDate.getTime() + 86400000), checkOutTime: '09:00' },
            combo3h: { checkInDate: formData.checkInDate, checkInTime: formData.checkInTime, checkOutDate: combo3hOut.date, checkOutTime: combo3hOut.time },
            combo6h1h: { checkInDate: formData.checkInDate, checkInTime: formData.checkInTime, checkOutDate: combo6h1hOut.date, checkOutTime: combo6h1hOut.time },
            slots: { checkInDate: formData.checkInDate, checkInTime: formData.checkInTime, checkOutDate: formData.checkOutDate, checkOutTime: formData.checkOutTime },
            block: { checkInDate: formData.checkInDate, checkInTime: formData.checkInTime, checkOutDate: formData.checkOutDate, checkOutTime: formData.checkOutTime },
        };
    });

    const upd = (u: Partial<BookingFormData>) => setFormData(p => ({ ...p, ...u }));
    const getRooms = (t: RoomType) => rooms.filter(r => r.type === t);

    const handleRoomChange = (id: string) => {
        const r = rooms.find(r => r.id === id);
        if (r) {
            const nextMode = r.bookingMode === 'block' ? 'block' : (formData.mode === 'block' ? 'hourly' : formData.mode);
            upd({
                roomId: r.id,
                roomName: r.name,
                roomType: r.type,
                mode: nextMode,
                bookingSlots: []
            });
        }
    };

    const handleModeChange = (mode: BookingMode) => {
        setModeData(p => ({ ...p, [formData.mode]: { checkInDate: formData.checkInDate, checkInTime: formData.checkInTime, checkOutDate: formData.checkOutDate, checkOutTime: formData.checkOutTime } }));
        const s = modeData[mode];
        const patch: Partial<BookingFormData> = { mode, checkInDate: s.checkInDate, checkInTime: s.checkInTime, checkOutDate: s.checkOutDate, checkOutTime: s.checkOutTime };
        if (mode === 'combo6h1h' && !formData.combo6h1hOption) {
            patch.combo6h1hOption = 'bonus_hour';
        }
        upd(patch);
    };

    /** Cập nhật combo6h1h option — nếu 'discount' thì rút còn 6h, 'bonus_hour' thì 7h */
    const handleCombo6h1hOptionChange = (option: Combo6h1hOption) => {
        const offset = option === 'discount' ? 6 : 7;
        const out = addHoursToCheckIn(formData.checkInDate, formData.checkInTime, offset);
        upd({ combo6h1hOption: option, checkOutDate: out.date, checkOutTime: out.time });
        setModeData(p => ({ ...p, combo6h1h: { ...p.combo6h1h, checkOutDate: out.date, checkOutTime: out.time } }));
    };

    const handleCheckInDateChange = (date: Date) => {
        const u: Partial<BookingFormData> = { checkInDate: date };
        if (formData.mode === 'daily' || formData.mode === 'overnight') {
            const nd = new Date(date); nd.setDate(nd.getDate() + 1); u.checkOutDate = nd;
        } else if (formData.mode === 'combo3h') {
            const out = addHoursToCheckIn(date, formData.checkInTime, 3);
            u.checkOutDate = out.date;
            u.checkOutTime = out.time;
        } else if (formData.mode === 'combo6h1h') {
            const offset = formData.combo6h1hOption === 'discount' ? 6 : 7;
            const out = addHoursToCheckIn(date, formData.checkInTime, offset);
            u.checkOutDate = out.date;
            u.checkOutTime = out.time;
        } else if (formData.mode === 'block') {
            const sortedAvailable = [...blockDefinitions].sort((a, b) => a.sortOrder - b.sortOrder);
            const finalSelected = sortedAvailable.filter(b => formData.bookingSlots?.includes(b.id));
            let hasOvernight = false;
            for (const sel of finalSelected) {
                const [sh, sm] = sel.startTime.split(':').map(Number);
                const [eh, em] = sel.endTime.split(':').map(Number);
                if (eh * 60 + em < sh * 60 + sm) {
                    hasOvernight = true;
                    break;
                }
            }
            if (hasOvernight) {
                const nd = new Date(date);
                nd.setDate(nd.getDate() + 1);
                u.checkOutDate = nd;
            } else {
                u.checkOutDate = date;
            }
        }
        upd(u);
        setModeData(p => ({ ...p, [formData.mode]: { ...p[formData.mode], checkInDate: date, checkOutDate: u.checkOutDate || formData.checkOutDate, checkOutTime: u.checkOutTime || formData.checkOutTime } }));
    };

    const handleTime = (f: 'checkInTime' | 'checkOutTime', v: string) => {
        upd({ [f]: v });
        setModeData(p => ({ ...p, [formData.mode]: { ...p[formData.mode], [f]: v } }));
    };

    const handleDate = (f: 'checkInDate' | 'checkOutDate', v: Date) => {
        upd({ [f]: v });
        setModeData(p => ({ ...p, [formData.mode]: { ...p[formData.mode], [f]: v } }));
    };

    const displayDuration = () => {
        const d = calculateDuration(formData.checkInDate, formData.checkInTime, formData.checkOutDate, formData.checkOutTime);
        if (formData.mode === 'daily') {
            const days = Math.max(1, Math.floor(d / 24) || 1);
            const extra = Math.max(0, Math.ceil(d - days * 24));
            return `${days} ngày${extra > 0 ? ` + ${extra}h` : ''}`;
        }
        if (formData.mode === 'overnight') {
            if (d <= 11) return '1 đêm';
            return `1 đêm + ${Math.ceil(d - 11)}h`;
        }
        if (formData.mode === 'combo3h') return '3 giờ';
        if (formData.mode === 'combo6h1h') {
            return formData.combo6h1hOption === 'discount' ? '6 giờ' : '6 giờ + 1H bonus';
        }
        if (formData.mode === 'slots') {
            return `${formData.bookingSlots?.length || 0} slot`;
        }
        if (formData.mode === 'block') {
            return `${formData.bookingSlots?.length || 0} ca`;
        }
        return `${d} giờ`;
    };

    const toLocalTimestamp = (dateStr: string, time: string): number => {
        const [y, M, d] = dateStr.split('-').map(Number);
        const [h, m] = time.split(':').map(Number);
        return new Date(y, M - 1, d, h, m, 0, 0).getTime();
    };

    const getBlockOccupiedType = (block: BlockDefinition): 'guest' | 'internal' | null => {
        const inDateStr = formatDateInput(formData.checkInDate);
        const blockStart = toLocalTimestamp(inDateStr, block.startTime);
        let blockEnd = toLocalTimestamp(inDateStr, block.endTime);
        const isOvernight = blockEnd <= blockStart;
        if (isOvernight) blockEnd += 24 * 60 * 60 * 1000;
        
        const roomBookings = bookings.filter(b => b.roomId === formData.roomId && b.status !== 'cancelled');
        let foundOccupied: 'guest' | 'internal' | null = null;

        for (const b of roomBookings) {
            const bStart = toLocalTimestamp(b.date, b.startTime);
            let bEnd = toLocalTimestamp(b.date, b.endTime);
            if (bEnd <= bStart) bEnd += 24 * 60 * 60 * 1000;
            
            let overlaps = false;
            if (blockStart < bEnd && bStart < blockEnd) {
                overlaps = true;
            } else if (isOvernight) {
                // Edge Case: if block is overnight (ends on next day),
                // and booking is daily or maintenance on that next day, consider it occupied!
                const [y, M, d] = inDateStr.split('-').map(Number);
                const nextDay = new Date(y, M - 1, d + 1);
                const nextDayStr = `${nextDay.getFullYear()}-${String(nextDay.getMonth() + 1).padStart(2, '0')}-${String(nextDay.getDate()).padStart(2, '0')}`;
                if (b.date === nextDayStr && (b.mode === 'daily' || b.internalTag === 'maintenance')) {
                    overlaps = true;
                }
            }

            if (overlaps) {
                if (b.category === 'internal') {
                    return 'internal'; // Highest priority: return immediately
                }
                foundOccupied = 'guest';
            }
        }
        return foundOccupied;
    };


    const handleBlockClick = (block: BlockDefinition) => {
        const sortedAvailable = [...blockDefinitions].sort((a, b) => a.sortOrder - b.sortOrder);
        let nextSlots = [...(formData.bookingSlots || [])];
        
        if (nextSlots.includes(block.id)) {
            nextSlots = nextSlots.filter(id => id !== block.id);
            const selectedDefs = sortedAvailable.filter(b => nextSlots.includes(b.id));
            if (selectedDefs.length > 0) {
                let isConsecutive = true;
                for (let i = 0; i < selectedDefs.length - 1; i++) {
                    const idx1 = sortedAvailable.findIndex(b => b.id === selectedDefs[i].id);
                    const idx2 = sortedAvailable.findIndex(b => b.id === selectedDefs[i+1].id);
                    if (idx2 - idx1 !== 1) {
                        isConsecutive = false;
                        break;
                    }
                }
                if (!isConsecutive) {
                    nextSlots = [];
                }
            }
        } else {
            if (nextSlots.length === 0) {
                nextSlots = [block.id];
            } else {
                const selectedDefs = sortedAvailable.filter(b => nextSlots.includes(b.id));
                const firstIdx = sortedAvailable.findIndex(b => b.id === selectedDefs[0].id);
                const lastIdx = sortedAvailable.findIndex(b => b.id === selectedDefs[selectedDefs.length - 1].id);
                const clickedIdx = sortedAvailable.findIndex(b => b.id === block.id);
                
                if (clickedIdx === firstIdx - 1) {
                    nextSlots = [block.id, ...nextSlots];
                } else if (clickedIdx === lastIdx + 1) {
                    nextSlots = [...nextSlots, block.id];
                } else {
                    nextSlots = [block.id];
                }
            }
        }
        
        const finalSelected = sortedAvailable.filter(b => nextSlots.includes(b.id));
        let checkInTime = formData.checkInTime;
        let checkOutTime = formData.checkOutTime;
        let checkOutDate = formData.checkInDate;
        
        if (finalSelected.length > 0) {
            checkInTime = finalSelected[0].startTime;
            checkOutTime = finalSelected[finalSelected.length - 1].endTime;
            
            let hasOvernight = false;
            for (const sel of finalSelected) {
                const [sh, sm] = sel.startTime.split(':').map(Number);
                const [eh, em] = sel.endTime.split(':').map(Number);
                if (eh * 60 + em < sh * 60 + sm) {
                    hasOvernight = true;
                    break;
                }
            }
            
            if (hasOvernight) {
                const nd = new Date(formData.checkInDate);
                nd.setDate(nd.getDate() + 1);
                checkOutDate = nd;
            } else {
                checkOutDate = formData.checkInDate;
            }
        }
        
        upd({
            bookingSlots: nextSlots,
            checkInTime,
            checkOutTime,
            checkOutDate
        });
    };

    const selectedRoom = rooms.find(r => r.id === formData.roomId);
    const combo6h1hDiscount = getRoomPriceConfig(selectedRoom ?? { type: formData.roomType }).combo6h1hDiscount;

    return (
        <div className="space-y-4 sm:space-y-5">
            <div>
                <h3 className="font-bold text-base sm:text-lg">Bước 1: Chọn phòng và thời gian</h3>
                <p className="text-sm my-1">Vui lòng chọn phòng và thời gian đặt phòng</p>
            </div>

            <div className="bg-white border rounded-lg shadow-sm">
                {/* Mode */}
                <div className="p-3 sm:p-4 border-b">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <label className="text-xs sm:text-sm font-medium text-gray-700 shrink-0">Hình thức đặt phòng:</label>
                        <div className="flex gap-2 flex-wrap">
                            {selectedRoom?.bookingMode === 'block' && blockDefinitions && blockDefinitions.length > 0 ? (
                                <button type="button" disabled
                                    className="px-3 py-1.5 rounded-md text-xs sm:text-sm font-semibold transition-all bg-zinc-900 text-white dark:bg-zinc-800 shadow-sm border-none">
                                    🕒 Đặt theo ca cố định
                                </button>
                            ) : (
                                <>
                                    {BOOKING_MODES.map(mode => (
                                        <button key={mode.value} type="button" onClick={() => handleModeChange(mode.value)}
                                            className={cn("px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all shadow-sm",
                                                formData.mode === mode.value ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground border border-border hover:bg-accent")}>
                                            {mode.label}
                                        </button>
                                    ))}
                                    {formData.mode === 'slots' && (
                                        <button type="button" disabled
                                            className="px-3 py-1.5 rounded-md text-xs sm:text-sm font-semibold transition-all bg-emerald-600 text-white shadow-sm border-none">
                                            ⚡ Khung giờ cố định
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {formData.mode === 'combo6h1h' && (
                        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                            <span className="text-xs sm:text-sm font-medium text-gray-700 shrink-0">Combo 6H+1H:</span>
                            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                                <label className={cn(
                                    "flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs sm:text-sm cursor-pointer transition-colors",
                                    (formData.combo6h1hOption ?? 'bonus_hour') === 'bonus_hour'
                                        ? "border-primary bg-primary/10 text-primary font-semibold"
                                        : "border-border bg-card text-gray-600 hover:bg-accent",
                                )}>
                                    <input
                                        type="radio"
                                        name="combo6h1hOption"
                                        className="size-3.5 accent-primary"
                                        checked={(formData.combo6h1hOption ?? 'bonus_hour') === 'bonus_hour'}
                                        onChange={() => handleCombo6h1hOptionChange('bonus_hour')}
                                    />
                                    <span>Nhận thêm 1 giờ bonus</span>
                                </label>
                                <label className={cn(
                                    "flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs sm:text-sm cursor-pointer transition-colors",
                                    formData.combo6h1hOption === 'discount'
                                        ? "border-primary bg-primary/10 text-primary font-semibold"
                                        : "border-border bg-card text-gray-600 hover:bg-accent",
                                )}>
                                    <input
                                        type="radio"
                                        name="combo6h1hOption"
                                        className="size-3.5 accent-primary"
                                        checked={formData.combo6h1hOption === 'discount'}
                                        onChange={() => handleCombo6h1hOptionChange('discount')}
                                    />
                                    <span>Giảm giá {formatPrice(combo6h1hDiscount)}đ thay thế</span>
                                </label>
                            </div>
                        </div>
                    )}

                    {formData.mode === 'slots' && formData.bookingSlots && formData.bookingSlots.length > 0 && (
                        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-100 rounded-lg flex flex-col gap-2 animate-fadeIn">
                            <div className="flex items-center gap-2">
                                <div className="flex items-center justify-center size-5 rounded-full bg-emerald-500 text-white shrink-0 shadow-sm">
                                    <span className="text-xs font-bold">⚡</span>
                                </div>
                                <span className="text-xs sm:text-sm font-semibold text-emerald-800">
                                    Khung giờ cố định đã chọn:
                                </span>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                {formData.bookingSlots.map(slotId => {
                                    let slotLabel = slotId;
                                    if (slotId === 'sang') slotLabel = 'Sáng (09:00 - 12:00)';
                                    else if (slotId === 'chieu1') slotLabel = 'Chiều 1 (12:30 - 15:30)';
                                    else if (slotId === 'chieu2') slotLabel = 'Chiều 2 (16:00 - 19:00)';
                                    else if (slotId === 'quadem') slotLabel = 'Qua đêm (19:30 - 07:30)';
                                    
                                    return (
                                        <span key={slotId} className="px-2.5 py-1 rounded bg-emerald-100 text-emerald-800 text-xs font-bold uppercase shadow-xs border border-emerald-200">
                                            {slotLabel}
                                        </span>
                                    );
                                })}
                            </div>
                            {formData.bookingSlots.length >= 2 && (
                                <p className="text-xs text-emerald-600 font-medium">
                                    🎁 Ưu đãi đặc biệt: Đặt {formData.bookingSlots.length} slot cùng lúc được giảm ngay {formData.bookingSlots.length === 2 ? '10%' : formData.bookingSlots.length === 3 ? '15%' : '20%'} tổng hóa đơn!
                                </p>
                            )}
                        </div>
                    )}

                    {formData.mode === 'block' && blockDefinitions && blockDefinitions.length > 0 && (
                        <div className="mt-3 p-3 bg-zinc-50 dark:bg-zinc-900 border border-border rounded-lg flex flex-col gap-3 animate-fadeIn">
                            <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-zinc-600" />
                                <span className="text-xs sm:text-sm font-semibold text-foreground">
                                    Danh sách ca đặt phòng (Chọn các ca liên tiếp):
                                </span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2.5">
                                {blockDefinitions.map((block) => {
                                    const isSelected = formData.bookingSlots?.includes(block.id);
                                    const occupiedType = getBlockOccupiedType(block);
                                    const isOccupied = occupiedType !== null;

                                    
                                    return (
                                        <button
                                            key={block.id}
                                            type="button"
                                            disabled={isOccupied}
                                            onClick={() => handleBlockClick(block)}
                                            className={cn(
                                                "relative p-3 rounded-lg border text-left transition-all duration-300 flex flex-col justify-between h-20 shadow-xs cursor-pointer",
                                                isSelected
                                                    ? "border-primary bg-primary/10 text-primary ring-2 ring-primary ring-offset-1 scale-[1.02] font-semibold"
                                                    : "border-border bg-card hover:bg-accent hover:border-zinc-400 text-foreground",
                                                isOccupied && "bg-zinc-100 dark:bg-zinc-800 border-dashed opacity-40 cursor-not-allowed text-muted-foreground hover:bg-zinc-100"
                                            )}
                                        >
                                            <div className="flex justify-between items-start w-full">
                                                <span className={cn("text-xs font-bold uppercase tracking-wider", isSelected ? "text-primary font-extrabold" : "text-foreground")}>
                                                    {block.name}
                                                </span>
                                                {isSelected && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
                                                {isOccupied && (
                                                    <span className={cn(
                                                        "text-[10px] font-bold uppercase px-1 rounded",
                                                        occupiedType === 'internal'
                                                            ? "text-amber-500 bg-amber-100 dark:bg-amber-950/40 dark:text-amber-300"
                                                            : "text-red-500 bg-red-100 dark:bg-red-950/40 dark:text-red-300"
                                                    )}>
                                                        {occupiedType === 'internal' ? 'Bảo trì' : 'Đã đặt'}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex justify-between items-end w-full mt-2">
                                                <span className={cn("text-xs font-mono", isSelected ? "text-primary/80 font-semibold" : "text-muted-foreground")}>
                                                    {block.startTime} - {block.endTime}
                                                </span>
                                                <span className={cn("text-xs font-bold", isSelected ? "text-primary" : "text-zinc-700 dark:text-zinc-300")}>
                                                    {formatPrice(block.price)}đ
                                                </span>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                            {formData.bookingSlots && formData.bookingSlots.length > 0 && (
                                <div className="mt-1 flex items-center gap-1.5 text-xs text-zinc-900 dark:text-zinc-100 font-medium">
                                    <AlertCircle className="h-3.5 w-3.5" />
                                    <span>
                                        Đã chọn dải giờ: {formData.checkInTime} - {formData.checkOutTime}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="p-3 sm:p-4">
                    {/* Desktop — flex với overflow-x-auto để không bao giờ bị che */}
                    <div className="hidden lg:block">
                        <div className="w-full">
                            {/* Header */}
                            <div className="flex items-center justify-between text-xs text-gray-500 font-medium mb-2 gap-2">
                                <div className="w-[90px] shrink-0">Hạng phòng</div>
                                <div className="flex-1 min-w-[150px]">Phòng</div>
                                <div className="w-[260px] shrink-0">Thời gian nhận</div>
                                <div className="w-[260px] shrink-0">Thời gian trả</div>
                                <div className="w-[52px] shrink-0 text-center">Dự kiến</div>
                                <div className="w-[90px] shrink-0 text-right">Thành tiền</div>
                            </div>
                            {/* Data */}
                            <div className="flex items-center justify-between gap-2">
                                <div className="w-[90px] shrink-0"><RoomTypeBadge type={formData.roomType} size="sm" /></div>
                                <div className="flex-1 min-w-[150px]">
                                    <Select value={formData.roomId} onValueChange={handleRoomChange}>
                                        <SelectTrigger className="w-full bg-white"><SelectValue /></SelectTrigger>
                                        <SelectContent position="popper" align="start">
                                            {getRooms(formData.roomType).map(r => (
                                                <SelectItem key={r.id} value={r.id}>
                                                    <span className="inline-flex items-center gap-2">
                                                        <span>{r.name}</span>
                                                        <RoomTypeBadge type={r.type} size="sm" />
                                                    </span>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="w-[260px] shrink-0 flex gap-1 items-center">
                                    <Input type="date" value={formatDateInput(formData.checkInDate)} onChange={e => handleCheckInDateChange(new Date(e.target.value))} className="bg-white flex-1 min-w-0" />
                                    {formData.mode === 'hourly' ? (
                                        <TimeSelects field="checkInTime" time={formData.checkInTime} onTimeChange={handleTime} />
                                    ) : formData.mode === 'block' ? (
                                        <div className="flex items-center gap-1 bg-zinc-100 text-zinc-400 border border-zinc-200 dark:bg-zinc-800 dark:text-zinc-500 rounded px-2.5 py-1 text-xs select-none font-mono font-medium shadow-2xs">
                                            <Lock className="size-3" /> {formData.checkInTime}
                                        </div>
                                    ) : (
                                        <span className="text-sm text-gray-600 font-medium shrink-0">{formData.checkInTime}</span>
                                    )}
                                </div>
                                <div className="w-[260px] shrink-0 flex gap-1 items-center">
                                    {formData.mode === 'block' ? (
                                        <div className="relative flex-1 min-w-0">
                                            <Input type="date" value={formatDateInput(formData.checkOutDate)} disabled className="bg-zinc-50 border-zinc-200 text-zinc-400 opacity-60 flex-1 min-w-0 pointer-events-none" />
                                            <Lock className="absolute right-2 top-2.5 size-3.5 text-zinc-400" />
                                        </div>
                                    ) : (
                                        <Input type="date" value={formatDateInput(formData.checkOutDate)} onChange={e => handleDate('checkOutDate', new Date(e.target.value))} className="bg-white flex-1 min-w-0" />
                                    )}
                                    {formData.mode === 'hourly' ? (
                                        <TimeSelects field="checkOutTime" time={formData.checkOutTime} onTimeChange={handleTime} />
                                    ) : formData.mode === 'block' ? (
                                        <div className="flex items-center gap-1 bg-zinc-100 text-zinc-400 border border-zinc-200 dark:bg-zinc-800 dark:text-zinc-500 rounded px-2.5 py-1 text-xs select-none font-mono font-medium shadow-2xs">
                                            <Lock className="size-3" /> {formData.checkOutTime}
                                        </div>
                                    ) : (
                                        <span className="text-sm text-gray-600 font-medium shrink-0">{formData.checkOutTime}</span>
                                    )}
                                </div>
                                <div className="w-[52px] shrink-0 text-center text-xs text-gray-600 font-medium whitespace-nowrap">{displayDuration()}</div>
                                <div className="w-[90px] shrink-0 text-right whitespace-nowrap flex flex-col justify-center items-end">
                                    {shortStayDiscount > 0 ? (
                                        <>
                                            <span className="text-xs text-gray-400 line-through font-normal">{formatPrice(originalPrice)}đ</span>
                                            <span className="text-sm font-bold text-emerald-600">{formatPrice(price)}đ</span>
                                        </>
                                    ) : (
                                        <span className="text-sm font-semibold text-primary">{formatPrice(price)}đ</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Mobile */}
                    <div className="lg:hidden space-y-4">
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-gray-700">Hạng phòng & Phòng</label>
                            <div className="flex gap-2">
                                <div className="w-28 shrink-0 px-3 py-2 bg-gray-50 rounded-md flex items-center justify-center"><RoomTypeBadge type={formData.roomType} size="sm" /></div>
                                <div className="flex-1">
                                    <Select value={formData.roomId} onValueChange={handleRoomChange}>
                                        <SelectTrigger className="w-full bg-white"><SelectValue /></SelectTrigger>
                                        <SelectContent position="popper" align="start">
                                            {getRooms(formData.roomType).map(r => (
                                                <SelectItem key={r.id} value={r.id}>
                                                    <span className="inline-flex items-center gap-2">
                                                        <span>{r.name}</span>
                                                        <RoomTypeBadge type={r.type} size="sm" />
                                                    </span>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-gray-700">Thời gian nhận</label>
                            <div className="flex gap-2 items-center">
                                <Input type="date" value={formatDateInput(formData.checkInDate)} onChange={e => handleCheckInDateChange(new Date(e.target.value))} className="bg-white flex-1" />
                                {formData.mode === 'hourly' && <TimeSelects field="checkInTime" time={formData.checkInTime} onTimeChange={handleTime} />}
                                {formData.mode === 'block' && (
                                    <div className="flex items-center gap-1 bg-zinc-100 text-zinc-400 border border-zinc-200 dark:bg-zinc-800 dark:text-zinc-500 rounded px-2.5 py-2 text-xs select-none font-mono font-medium shadow-2xs shrink-0">
                                        <Lock className="size-3" /> {formData.checkInTime}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-medium text-gray-700">Thời gian trả</label>
                            <div className="flex gap-2 items-center">
                                {formData.mode === 'block' ? (
                                    <div className="relative flex-1">
                                        <Input type="date" value={formatDateInput(formData.checkOutDate)} disabled className="bg-zinc-50 border-zinc-200 text-zinc-400 opacity-60 flex-1 pointer-events-none" />
                                        <Lock className="absolute right-2 top-2.5 size-3.5 text-zinc-400" />
                                    </div>
                                ) : (
                                    <Input type="date" value={formatDateInput(formData.checkOutDate)} onChange={e => handleDate('checkOutDate', new Date(e.target.value))} className="bg-white flex-1" />
                                )}
                                {formData.mode === 'hourly' && <TimeSelects field="checkOutTime" time={formData.checkOutTime} onTimeChange={handleTime} />}
                                {formData.mode === 'block' && (
                                    <div className="flex items-center gap-1 bg-zinc-100 text-zinc-400 border border-zinc-200 dark:bg-zinc-800 dark:text-zinc-500 rounded px-2.5 py-2 text-xs select-none font-mono font-medium shadow-2xs shrink-0">
                                        <Lock className="size-3" /> {formData.checkOutTime}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t">
                            <span className="text-sm font-medium text-gray-700">Dự kiến: {displayDuration()}</span>
                            {shortStayDiscount > 0 ? (
                                <div className="flex flex-col items-end">
                                    <span className="text-xs text-gray-400 line-through font-normal">{formatPrice(originalPrice)}đ</span>
                                    <span className="text-base font-bold text-emerald-600">{formatPrice(price)}đ</span>
                                </div>
                            ) : (
                                <span className="text-base font-bold text-primary">{formatPrice(price)}đ</span>
                            )}
                        </div>
                    </div>

                    {shortStayDiscount > 0 && (
                        <div className="mt-4 p-3 bg-emerald-50 border border-emerald-100 rounded-lg flex items-start gap-2.5 animate-fadeIn">
                            <div className="flex items-center justify-center size-5 rounded-full bg-emerald-500 text-white shrink-0 mt-0.5 shadow-sm">
                                <span className="text-xs font-bold font-mono">%</span>
                            </div>
                            <div className="space-y-0.5">
                                <p className="text-xs sm:text-sm font-semibold text-emerald-800">
                                    Ưu đãi đặt phòng ngắn hạn đã được áp dụng!
                                </p>
                                <p className="text-xs text-emerald-600 font-medium">
                                    {duration < 2 
                                        ? "Đặt phòng dưới 2 tiếng được giảm ngay 20.000đ vào tổng hóa đơn."
                                        : "Đặt phòng dưới 3 tiếng được giảm ngay 10.000đ vào tổng hóa đơn."
                                    }
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Room Amenities */}
            {(() => {
                const selectedRoom = rooms.find(r => r.id === formData.roomId);
                const amenities = selectedRoom?.amenities;
                if (!amenities || amenities.length === 0) return null;
                return (
                    <div className="bg-white border rounded-lg shadow-sm px-3 sm:px-4 py-4 sm:py-5">
                        <h4 className="font-semibold text-gray-800 text-sm sm:text-base mb-3">Tiện nghi phòng {selectedRoom.name}</h4>
                        <div className="flex flex-wrap gap-2">
                            {amenities.map((amenity, i) => {
                                const warn = isSharedWC(amenity);
                                return (
                                    <span
                                        key={i}
                                        className={cn(
                                            "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs sm:text-sm",
                                            warn
                                                ? "bg-red-100 text-red-700 border border-red-200 font-semibold"
                                                : "bg-muted text-foreground",
                                        )}
                                    >
                                        <span aria-hidden="true">{getAmenityIcon(amenity)}</span>
                                        <span>{amenity}</span>
                                    </span>
                                );
                            })}
                        </div>
                        {hasSharedWC(amenities) && (
                            <p className="mt-2 text-xs font-medium text-red-600">
                                ⚠️ {SHARED_WC_WARNING}
                            </p>
                        )}
                    </div>
                );
            })()}

            {/* Food */}
            <div className="bg-white border rounded-lg shadow-sm px-3 sm:px-4 py-4 sm:py-5">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-4">
                    <div className="flex-1">
                        <h4 className="font-semibold text-gray-800 text-base sm:text-lg mb-2">Dịch vụ thêm</h4>
                        <p className="text-xs sm:text-sm text-gray-600 mb-1">Phòng không có sẵn dịch vụ thêm.</p>
                        <p className="text-xs sm:text-sm text-gray-600">Tình iu vui lòng đặt trước để tụi mình chuẩn bị nghen 🍹 🍰</p>
                    </div>
                    <button type="button" onClick={() => onOpenFoodModal?.()}
                        className="flex font-semibold text-white cursor-pointer items-center justify-center gap-2 bg-status-warning px-3 sm:px-4 py-2 rounded-md shadow hover:bg-status-warning/90 transition-colors whitespace-nowrap text-sm w-full sm:w-auto">
                        <ShoppingCart className="w-4 h-4" /> Thêm dịch vụ
                    </button>
                </div>

            </div>
        </div>
    );
};
